import { Mic, MicOff, Video, VideoOff, Monitor, MonitorOff, PhoneOff, Users, Settings2, Sparkles, Loader2 } from 'lucide-react'

interface MeetingControlsProps {
  isMuted: boolean
  isCameraOff: boolean
  isScreenSharing: boolean
  isBackgroundBlurEnabled: boolean
  isBackgroundBlurLoading: boolean
  showParticipants: boolean
  participantCount: number
  onToggleMute: () => void
  onToggleCamera: () => void
  onToggleScreenShare: () => void
  onToggleBackgroundBlur: () => void
  onToggleParticipants: () => void
  onOpenSettings: () => void
  onLeave: () => void
}

export function MeetingControls({
  isMuted,
  isCameraOff,
  isScreenSharing,
  isBackgroundBlurEnabled,
  isBackgroundBlurLoading,
  showParticipants,
  participantCount,
  onToggleMute,
  onToggleCamera,
  onToggleScreenShare,
  onToggleBackgroundBlur,
  onToggleParticipants,
  onOpenSettings,
  onLeave,
}: MeetingControlsProps) {
  return (
    <div className="flex items-center justify-center gap-3 p-4 bg-[var(--bg-card)] border-t border-[var(--border)]">
      {/* Mute button */}
      <button
        onClick={onToggleMute}
        className={`
          p-4 rounded-full transition-all duration-200
          ${isMuted
            ? 'bg-red-500 hover:bg-red-600 text-white'
            : 'bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] text-[var(--text-main)]'
          }
        `}
        title={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? <MicOff size={22} /> : <Mic size={22} />}
      </button>

      {/* Camera button */}
      <button
        onClick={onToggleCamera}
        className={`
          p-4 rounded-full transition-all duration-200
          ${isCameraOff
            ? 'bg-red-500 hover:bg-red-600 text-white'
            : 'bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] text-[var(--text-main)]'
          }
        `}
        title={isCameraOff ? 'Turn on camera' : 'Turn off camera'}
      >
        {isCameraOff ? <VideoOff size={22} /> : <Video size={22} />}
      </button>

      {/* Screen share button */}
      <button
        onClick={onToggleScreenShare}
        className={`
          p-4 rounded-full transition-all duration-200
          ${isScreenSharing
            ? 'bg-[var(--primary)] hover:opacity-90 text-white'
            : 'bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] text-[var(--text-main)]'
          }
        `}
        title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
      >
        {isScreenSharing ? <MonitorOff size={22} /> : <Monitor size={22} />}
      </button>

      {/* Background blur button */}
      <button
        onClick={onToggleBackgroundBlur}
        disabled={isBackgroundBlurLoading || isCameraOff}
        className={`
          p-4 rounded-full transition-all duration-200
          ${isBackgroundBlurEnabled
            ? 'bg-[var(--primary)] hover:opacity-90 text-white'
            : 'bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] text-[var(--text-main)]'
          }
          ${(isBackgroundBlurLoading || isCameraOff) ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        title={isCameraOff ? 'Turn on camera to use blur' : isBackgroundBlurEnabled ? 'Disable background blur' : 'Enable background blur'}
      >
        {isBackgroundBlurLoading ? (
          <Loader2 size={22} className="animate-spin" />
        ) : (
          <Sparkles size={22} />
        )}
      </button>

      {/* Participants button */}
      <button
        onClick={onToggleParticipants}
        className={`
          relative p-4 rounded-full transition-all duration-200
          ${showParticipants
            ? 'bg-[var(--primary)] hover:opacity-90 text-white'
            : 'bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] text-[var(--text-main)]'
          }
        `}
        title="Participants"
      >
        <Users size={22} />
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--primary)] text-white text-xs rounded-full flex items-center justify-center">
          {participantCount}
        </span>
      </button>

      {/* Settings button */}
      <button
        onClick={onOpenSettings}
        className="p-4 rounded-full bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] text-[var(--text-main)] transition-all duration-200"
        title="Settings"
      >
        <Settings2 size={22} />
      </button>

      {/* Leave button */}
      <button
        onClick={onLeave}
        className="p-4 rounded-full bg-red-600 hover:bg-red-700 text-white transition-all duration-200"
        title="Leave meeting"
      >
        <PhoneOff size={22} />
      </button>
    </div>
  )
}
